import { type ApolloClient, type NormalizedCacheObject, type QueryResult, useSubscription } from "@apollo/client";
import * as Sentry from "@sentry/react";
import { LockState, type Maybe } from "gql/graphql";
import { handleSetInitialData, useInitialStateQuery } from "hooks/device/useInitialStateQuery";
import { useDoorAndLockState } from "hooks/useDoorAndLockState";
import { useFetchUnknownDishTypes } from "hooks/useFetchUnknownDishTypes";
import { useMixpanelTrackers } from "hooks/useMixpanelTrackers";
import { useRefetchDishTypesQuery } from "hooks/useRefetchDishTypesQuery";
import { useAppDispatch, useAppSelector, useAppStore } from "hooks/utilsHooks/storeHooks";
import { createContext, type PropsWithChildren, useContext, useEffect, useRef } from "react";
import { batch } from "react-redux";
import { captureApolloError } from "services/external/sentry";
import { setQrCode } from "slices/accessSlice";
import { setDishTypes } from "slices/dishesSlice";
import { setDoorLockState, setDoorState, setHasDoorBeenOpenedDuringOrder, setLockState } from "slices/doorSlice";
import { setFridgeState } from "slices/fridgeInfoSlice";
import { setLightingMode } from "slices/fridgeLightingSlice";
import { selectOrder, setTransaction } from "slices/transactionSlice";
import {
	DoorState,
	type FridgeSubscription,
	LightingSegmentModeType,
	type Subscription,
	TransactionState,
} from "types";
import type { LocalFridgeState } from "types/mainTypes";
import { getFridgeId, getTabletTypeFromUrl, validateFridgeId } from "utils/browser/localStorage";
import { debugTrace, shouldTraceLightingDebug } from "utils/debug/debugTrace";
import { FRIDGE_SUBSCRIPTION, INITIAL_STATE_QUERY } from "utils/gqlOperations";
import { logToSentry } from "utils/logger";
import { getRefillTransitions } from "utils/refillTransitions";
import { deepOmit } from "utils/utils";

export type DoorStateType = {
	state: DoorState | undefined;
	lock: {
		state: LockState | undefined;
		timeout: number | undefined | null;
	};
};

export type DeviceContextType = {
	id: string;
	loading: boolean;
	appConnected: boolean;
	handleRefetchDishTypes: () => Promise<void>;
	door: DoorStateType;
	initialQueryData: QueryResult | null;
};

type DeviceProviderProps = {
	client: ApolloClient<NormalizedCacheObject> | null;
	appConnected: boolean;
	isSubscriptionBroken: boolean;
	setIsSubscriptionBroken: (newState: boolean) => void;
};

const initialState: DeviceContextType = {
	id: "",
	loading: true,
	appConnected: true,
	handleRefetchDishTypes: () => Promise.resolve(),
	door: {
		state: DoorState.Closed,
		lock: {
			state: LockState.Locked,
			timeout: 0,
		},
	},
	initialQueryData: null,
};

export const DeviceContext = createContext<DeviceContextType>({
	...initialState,
	appConnected: true,
	loading: true,
});

export const DeviceContextProvider = ({
	children,
	appConnected,
	isSubscriptionBroken,
	setIsSubscriptionBroken,
}: PropsWithChildren<DeviceProviderProps>) => {
	getTabletTypeFromUrl();
	const fridgeId = getFridgeId() ?? "";
	const appDispatch = useAppDispatch();
	// Used by onData for synchronous prev/next slot reads — dispatch is synchronous, so
	// store.getState() is never stale, unlike a closed-over selector value when rapid
	// subscription pushes arrive before React re-renders.
	const store = useAppStore();
	const order = useAppSelector(selectOrder);
	const { doors, lock, lockTimeout } = useDoorAndLockState();
	const { trackDoorStateChange, trackRefillStarted, trackRefillFinished } = useMixpanelTrackers();
	const { getUnknownDishTypes } = useFetchUnknownDishTypes();
	const lastTrackedDoorStateRef = useRef<DoorState | null>(null);

	const {
		data,
		loading: initialQueryLoading,
		refetch: initialQueryRefetch,
	} = useInitialStateQuery({
		fridgeId: fridgeId,
	});
	const {
		loading: refetchDishTypesLoading,
		handleRefetchDishTypes,
		isDeferredRefetchPending,
	} = useRefetchDishTypesQuery({
		fridgeId: fridgeId,
	});

	useEffect(() => {
		// in case of a deferred refetch wait for order to be finished
		const isOrderActive = order?.state !== TransactionState.Finished;
		if (isDeferredRefetchPending && !isOrderActive) {
			handleRefetchDishTypes();
		}
	}, [isDeferredRefetchPending, order, handleRefetchDishTypes]);

	const shouldSkipSubscription = !data?.fridge || Boolean(data.fridge?.error) || !validateFridgeId(fridgeId);

	useSubscription<Subscription>(FRIDGE_SUBSCRIPTION, {
		// If there is an error in the initial state query, we do not connect subscription
		skip: shouldSkipSubscription,
		variables: { id: fridgeId },
		// By using onData callback, we handle situation when messages from API
		// are delivered in the same time
		onData: ({ data }) => {
			if (data?.data?.fridge?.error) {
				logToSentry(`Error is subscription, fridge: ${fridgeId}`, "error", [data.data.fridge.error]);
			}
			setIsSubscriptionBroken(false);
			const cleanedData = deepOmit<Maybe<FridgeSubscription> | undefined, LocalFridgeState>(data.data?.fridge, [
				"__typename",
			]);
			const transaction = cleanedData.transaction;
			const isLastOrderFinished = transaction?.orders?.last?.state === TransactionState.Finished;
			const hasDishTypes = cleanedData?.dishes?.types;

			// TEMP diagnostic: log what each FRIDGE_SUBSCRIPTION frame actually carries for lighting, so a
			// capture shows the BE-pushed state directly (not just the derived Redux writes). Pair with the
			// converger's FIRE-SWITCH / SWITCH-OK / FIRE-TOGGLE lines to see send vs ack vs subscription.
			if (shouldTraceLightingDebug()) {
				const dishTypes = cleanedData.dishes?.types ?? null;
				const spotlit = dishTypes
					? dishTypes.filter((dishType) => dishType?.lighting?.mode === LightingSegmentModeType.Spotlight).length
					: "-";
				debugTrace(
					`[state-debug] SUB lighting.mode=${cleanedData.lighting?.mode ?? "-"} dishTypes=${dishTypes?.length ?? "-"} spot=${spotlit} order=${transaction?.orders?.last?.state ?? "-"} refill=${transaction?.refills?.last?.state ?? "-"} door=${cleanedData.door?.state ?? cleanedData.door?.lock?.state ?? "-"}`,
				);
			}

			batch(() => {
				if (cleanedData.state != null) {
					appDispatch(setFridgeState(cleanedData.state));
				}

				const hasDoorStateChange = cleanedData.door?.lock?.state;

				if (cleanedData.access?.qrCode?.code) {
					appDispatch(setQrCode(cleanedData.access.qrCode.code));
				}
				if (hasDoorStateChange) {
					if (cleanedData.door.state && cleanedData.door.state !== lastTrackedDoorStateRef.current) {
						trackDoorStateChange(cleanedData.door.state);
						lastTrackedDoorStateRef.current = cleanedData.door.state;
					}
					appDispatch(setDoorLockState(cleanedData.door));
				} else if (cleanedData?.door?.state) {
					if (cleanedData.door.state !== lastTrackedDoorStateRef.current) {
						trackDoorStateChange(cleanedData.door.state);
						lastTrackedDoorStateRef.current = cleanedData.door.state;
					}
					appDispatch(setDoorState(cleanedData.door.state));
				} else if (cleanedData?.door?.lock?.state) {
					appDispatch(setLockState(cleanedData.door.lock?.state));
				}

				if (cleanedData.lighting?.mode != null) {
					appDispatch(setLightingMode(cleanedData.lighting.mode));
				}

				if (cleanedData.dishes?.types) {
					getUnknownDishTypes(cleanedData.dishes.types);
				}

				if (transaction?.orders?.last || transaction?.refills?.last) {
					const prevSlot = store.getState().transaction.slot;
					appDispatch(
						setTransaction({
							order: transaction.orders?.last ?? null,
							refill: transaction.refills?.last ?? null,
						}),
					);
					const nextSlot = store.getState().transaction.slot;
					// A different transaction id taking the slot means a new flow starts: reset the door flag
					// (covers cross-kind takeovers too). hasDoorBeenOpenedDuringOrder is shared between order
					// and refill flows because useOrderSteps drives both via selectActiveLocalTransaction.
					if (prevSlot && nextSlot && prevSlot.transaction.id !== nextSlot.transaction.id) {
						appDispatch(setHasDoorBeenOpenedDuringOrder(false));
					}
					if (nextSlot?.kind === "refill") {
						const prevRefill = prevSlot?.kind === "refill" ? prevSlot.transaction : null;
						const { isNewRefill, isRefillFinished } = getRefillTransitions(prevRefill, nextSlot.transaction);
						// Finished guard: reconnect echoes of old finished refills must not emit a spurious "started".
						if (isNewRefill && nextSlot.transaction.state !== TransactionState.Finished) {
							logToSentry(`Refill started on fridge: ${fridgeId}, mode: ${nextSlot.transaction.mode}`, "info");
							trackRefillStarted(nextSlot.transaction);
						}
						if (isRefillFinished) {
							logToSentry(`Refill finished on fridge: ${fridgeId}, mode: ${nextSlot.transaction.mode}`, "info");
							trackRefillFinished(nextSlot.transaction);
						}
					}
				}

				if (hasDishTypes || isLastOrderFinished) {
					// In case of last dish in the fridge being taken out, we receive null
					// in dishes.types array, so we set dishTypes to emptyArray
					appDispatch(
						setDishTypes({
							dishTypes: hasDishTypes ?? [],
							isOrder: isLastOrderFinished,
						}),
					);
				}
			});
		},
	});

	const handleReconnectAfterBrokenSubscription = async (retryCount: number) => {
		if (retryCount > 2) {
			captureApolloError(new Error("Failed to load initial fridge state 3 times"), {
				kind: "Initial_Query_Error_Retries_Limit",
				query: INITIAL_STATE_QUERY.kind,
				variables: { fridgeId: fridgeId },
			});
			return;
		}
		try {
			logToSentry(`Sending initial query after broken subscription on fridge: ${fridgeId}`, "warning");
			const res = await initialQueryRefetch();
			if (res.data.fridge) {
				logToSentry(`Setting initial data after broken subscription fridge: ${fridgeId}`, "warning", res.data.fridge);
				handleSetInitialData(res.data?.fridge, appDispatch);
			} else {
				captureApolloError(
					new Error("Initial query error"),
					{
						kind: "Initial_Query_Error",
						query: INITIAL_STATE_QUERY.kind,
						variables: { fridgeId: fridgeId },
					},
					{
						ResponseData: res,
					},
				);
				setTimeout(() => {
					handleReconnectAfterBrokenSubscription(retryCount + 1);
				}, 1000);
			}
		} catch (err) {
			Sentry.captureException(err);
		}
	};

	useEffect(() => {
		const reconnect = async () => {
			if (isSubscriptionBroken && appConnected && fridgeId) {
				await handleReconnectAfterBrokenSubscription(0);
			}
		};
		reconnect();
		// biome-ignore lint/correctness/useExhaustiveDependencies: handleReconnectAfterBrokenSubscription is stable enough
	}, [isSubscriptionBroken, appConnected, fridgeId, handleReconnectAfterBrokenSubscription]);

	return (
		<DeviceContext.Provider
			value={{
				id: fridgeId,
				appConnected: appConnected,
				loading: initialQueryLoading || refetchDishTypesLoading,
				door: {
					state: doors,
					lock: {
						state: lock,
						timeout: lockTimeout,
					},
				},
				handleRefetchDishTypes: handleRefetchDishTypes,
				initialQueryData: data,
			}}
		>
			{children}
		</DeviceContext.Provider>
	);
};

export const useDeviceContext = () => useContext(DeviceContext);
